import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, SelectionImageMap } from './components/ImageMap.tsx';
import AssistanceCanvas from './components/AssistanceCanvas.tsx';
import classnames from '../../../core/utils/classnames';
import { useAssistanceContext } from './AssistanceContext';
import { useAssistanceViewContext } from './AssistanceViewContext';
import { useInteractionSelector } from './interactionStore';
import { GROUP_SEPARATOR } from './useDocumentConfig';

const AssistanceImageMap = ({
    ocr,
    formsRef,
    previewRef,
    items,
    fields,
    isEmailOcr = false,
    spacingTop = 100,
}: any) => {
    const { t } = useTranslation('assistance');
    const { onFieldReselectStart, onFieldReselectEnd, onFieldBlur } = useAssistanceViewContext();
    const { handlers } = useAssistanceContext();

    // the image map highlights all interaction state, so it subscribes to the whole store
    const { focusField, hoverField, hoverItemIndex, reselectField } = useInteractionSelector((s) => s);

    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

    const handleSelectionStart = useCallback(() => {
        onFieldReselectStart({ ...focusField });
    }, [focusField]);

    const handleSelectionEnd = useCallback(
        (bbox: any, pageIndex: number) => {
            // simulate the call coming from the field not the image map
            reselectField?.reselectTargetRef?.current?.dispatchEvent(
                new CustomEvent('reselect', {
                    detail: { isEmailOcr, bbox, pageIndex },
                })
            );

            // clean up
            onFieldReselectEnd(null);
            onFieldBlur(null);
        },
        [handlers?.onReselect, reselectField, isEmailOcr]
    );

    const handleSelectionCancel = useCallback(() => {
        onFieldReselectEnd(null);
        onFieldBlur(null);
    }, [onFieldReselectEnd, onFieldBlur]);

    const scrollFormToItem = (itemIndex: number) => {
        const triggers = formsRef?.current?.querySelectorAll('.js-form-item-group');
        const trigger = triggers?.[itemIndex];
        if (!trigger) return;

        trigger.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleImageLoad = (evt) => {
        const { width, height } = evt.currentTarget;
        setImageDimensions({ width, height });
    };

    const [imageSrc, setImageSrc] = useState(ocr?.processedFileUrl);
    useEffect(() => {
        setImageSrc(ocr?.processedFileUrl);
    }, [ocr?.processedFileUrl]);

    const canSelect =
        (focusField !== null && focusField?.reselectTargetRef?.current) ||
        (reselectField !== null && reselectField.reselectTargetRef?.current);
    const canSelectAcrossPages = (reselectField ?? focusField)?.canSelectAcrossPages;

    // reselectField is only set while a drag is in progress
    let selectionHint = null;
    if (reselectField) selectionHint = t('assistance.selectionHint.dragging');
    else if (canSelect && canSelectAcrossPages) selectionHint = t('assistance.selectionHint.acrossPages');
    else if (canSelect) selectionHint = t('assistance.selectionHint.default');

    return (
        <AssistanceCanvas
            artboardWidth={imageDimensions.width}
            artboardHeight={imageDimensions.height}
            spacingTop={spacingTop}
            viewportRef={previewRef}
            hint={selectionHint}
        >
            <SelectionImageMap
                src={imageSrc}
                numPages={ocr?.pagesCount}
                onLoad={handleImageLoad}
                canSelect={canSelect}
                canSelectAcrossPages={canSelectAcrossPages}
                onSelectionStart={handleSelectionStart}
                onSelectionEnd={handleSelectionEnd}
                onSelectionCancel={handleSelectionCancel}
            >
                {!isEmailOcr &&
                    items.map((item: any, itemIndex: number) => (
                        <Area
                            key={itemIndex}
                            data={item}
                            bbox={item?.bbox}
                            pageIndex={item?.pageIndex}
                            onClick={() => scrollFormToItem(itemIndex)}
                            className={classnames(
                                'js-image-map-row fill-transparent stroke-1 stroke-[rgba(0,0,0,0.4)] opacity-80',
                                focusField && focusField.itemIndex === itemIndex && 'opacity-80 fill-[#c5ffed]',
                                hoverField && hoverField.itemIndex === itemIndex && 'opacity-80 fill-[#c5ffed]',
                                hoverItemIndex === itemIndex && 'opacity-80 fill-[#c5ffed]'
                            )}
                        />
                    ))}

                {reselectField === null &&
                    fields
                        ?.filter(
                            (field) =>
                                field.data?.isEmailOcr === isEmailOcr ||
                                (isEmailOcr === false && field.data?.isEmailOcr == null)
                        )
                        .map((field, fieldIndex) => (
                            <Area
                                key={fieldIndex}
                                data={field?.data}
                                bbox={field?.bbox}
                                pageIndex={field?.pageIndex}
                                className={classnames(
                                    'js-image-map-text stroke-1 stroke-surface-success-strong fill-[#c5ffed] opacity-80',
                                    focusField &&
                                        (focusField.fieldName?.split(GROUP_SEPARATOR)[0] !== field.fieldName ||
                                            focusField.itemIndex !== field.itemIndex) &&
                                        'fill-transparent',
                                    hoverField &&
                                        !(
                                            hoverField.fieldName?.split(GROUP_SEPARATOR)[0] === field.fieldName &&
                                            hoverField.itemIndex === field.itemIndex
                                        ) &&
                                        'fill-transparent'
                                )}
                            />
                        ))}
            </SelectionImageMap>
        </AssistanceCanvas>
    );
};

export default AssistanceImageMap;
